import React, { Suspense } from 'react'
import { Outlet, Navigate, useLocation } from 'react-router'
import { useAuth, getAuthEnvironmentInfo } from '@/shared/auth/unified-auth'
import { chatHomeForward, isGuestRole } from '@agion/auth'
import { GuestNotice } from '@/shared/auth/GuestNotice'
import { HomeForward } from '@/shared/auth/HomeForward'
import { getRequiredConfig } from '@/shared/config/runtime-env'
import { Sidebar } from './Sidebar'
import { BudgetAlertBanner } from '@/components/BudgetAlertBanner'
import { IngestionHealthBanner } from '@/components/IngestionHealthBanner'
import { AgionAssistant, isAdminMCPEnabled } from '@/components/assistant'
import { cn } from '@/shared/lib/variants'
import { useGlobalSearch } from '@/hooks/useGlobalSearch'
import { useAssistantStore } from '@/shared/stores'
import { shouldShowBudgetAlertBanner, shouldShowIngestionHealthBanner } from './layout-utils'

// Fallback for initial page load (Suspense boundary)
// This only shows on first load - subsequent navigations keep previous content
const PageLoadingFallback: React.FC = () => (
  <div className="flex h-full items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
  </div>
)

const narrowViewportQuery = '(max-width: 767px)'

export const MainLayout: React.FC = () => {
  const { isAuthenticated, user } = useAuth()
  const location = useLocation()
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false)
  const [narrowViewport, setNarrowViewport] = React.useState(
    () => window.matchMedia(narrowViewportQuery).matches,
  )
  const { isDemo, useMockData, mockAuthEnabled, authDisabled } = getAuthEnvironmentInfo()
  const openAssistant = useAssistantStore((s) => s.open)
  const adminMCPEnabled = isAdminMCPEnabled()

  React.useEffect(() => {
    const mediaQuery = window.matchMedia(narrowViewportQuery)
    const syncViewport = () => setNarrowViewport(mediaQuery.matches)
    syncViewport()
    mediaQuery.addEventListener('change', syncViewport)
    return () => mediaQuery.removeEventListener('change', syncViewport)
  }, [])

  const effectiveSidebarCollapsed = narrowViewport || sidebarCollapsed

  // Global Cmd+K / Ctrl+K shortcut to open assistant
  useGlobalSearch(openAssistant, adminMCPEnabled)

  // Check if we're in any form of demo/mock mode
  const isDemoMode = isDemo || useMockData || mockAuthEnabled === 'true' || authDisabled === 'true'

  // Production: Authentication is always required (no bypass flags)
  // Only skip redirect if in demo/mock mode for local development
  if (!isAuthenticated && !isDemoMode) {
    // In production, redirect to the centralized login UI
    if (window.location.hostname.includes('admin.')) {
      const loginUrl = getRequiredConfig('VITE_LOGIN_URL', 'http://localhost:3000')
      window.location.href = loginUrl
      return null
    }
    // For local development
    return <Navigate to="/login" replace />
  }

  // Organization guard: authenticated users without an organization
  // should not see the admin dashboard. SessionValidator navigates them
  // to the correct onboarding page (pending vs create-org), but there's
  // a brief render cycle where MainLayout mounts before the navigate commits.
  // INVARIANT: Return null here — do NOT redirect to a specific onboarding page.
  // Hardcoding /create-organization caused pending_approval users to be sent
  // to the wrong page because this guard fired before SessionValidator's
  // navigate('/onboarding/pending') took effect.
  if (isAuthenticated && !isDemoMode && !user?.organizationId) {
    return null
  }

  // A member whose home the server names as the chat app is forwarded there
  // (ADR-20260930 D7). Without a home from the server (it could not read the
  // membership), a Guest still sees the notice with the way to the chat app.
  const homeForward = chatHomeForward(user)
  if (homeForward) {
    return <HomeForward to={homeForward} />
  }
  if (isGuestRole(user?.role)) {
    return <GuestNotice />
  }

  return (
    <div className="flex h-screen bg-[var(--background)]">
      {/* Sidebar - ChatGPT style - Never re-renders during navigation */}
      <aside
        className={cn(
          "bg-slate-50 dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700/50 flex-shrink-0 transition-all duration-300",
          "md:block",
          effectiveSidebarCollapsed ? "w-16" : "w-64"
        )}
        // eslint-disable-next-line i18next/no-literal-string
        aria-label="Main navigation"
      >
        <Sidebar
          collapsed={effectiveSidebarCollapsed}
          showCollapseToggle={!narrowViewport}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      </aside>

      {/* Main content - single Suspense boundary for all pages */}
      {/* By having ONE Suspense here instead of per-route, React keeps the previous
          page content visible during navigation (no flash). The fallback only shows
          on initial load when there's no previous content to display. */}
      <main className="min-w-0 flex-1 overflow-auto bg-[var(--background)]">
        {shouldShowBudgetAlertBanner(location.pathname) && <BudgetAlertBanner />}
        {shouldShowIngestionHealthBanner(location.pathname) && <IngestionHealthBanner />}
        <Suspense fallback={<PageLoadingFallback />}>
          <Outlet />
        </Suspense>
      </main>

      {adminMCPEnabled && <AgionAssistant />}
    </div>
  )
}
